/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable indent */
import i18n, { t } from 'i18next'

import { getRetailMediaAccountType } from '~/helpers'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import { formatRoasCutoffDate } from '~/modules/retailMedia/constants'
import { buildNtbTooltipValues } from '~/modules/retailMedia/helpers/ntbMetrics'

import { ImpressionShareMetricCell } from '../../_elements/ImpressionShareMetricCell'
import { MetricTooltip } from '../../../MetricMethodologyTooltip'
import AdInfo from './elements/AdInfo'
import Status from './elements/Status'
import { toggleValueByStatus } from './helpers'
import { ntbAdColumns } from './ntbColumns'

const accountType = getRetailMediaAccountType()

export const commonColumns: CommonColumns = (
  availableMetrics = {
    impressionShare: false,
    lostIsRank: false,
    lostIsBudget: false,
    newToBrandCustomers: false,
    newToBrandRevenue: false
  } as NewMetricsAvailability,
  showClickAlert = false,
  campaignAdType = undefined,
  lookbackMonths = undefined,
  ntbOrdersWindow = undefined
) => ({
  /**
   * Dados do anúncio
   */
  title: {
    title: t('rm:Ads'),
    sortKey: 'name',
    render: AdInfo,
    showTotal: true
  },

  /**
   * Status do anúncio e indicação de veiculação
   */
  status: {
    title: t('rm:Status'),
    sortKey: 'ad_status',
    render: Status
  },

  /**
   * Métricas
   */
  impressions: {
    title: t('rm:metrics.impressions'),
    helpText: t('rm:metrics.impressions.tooltip'),
    sortKey: 'impressions',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { impressions: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.impressions)
  },
  views: {
    title: t('rm:metrics.views'),
    helpText: t('rm:metrics.views.tooltip'),
    sortKey: 'views',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { views: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.views)
  },
  clicks: {
    title: t('rm:metrics.clicks'),
    // Offsite clicks are tracked on the destination publisher's site, not
    // on the retailer's own storefront — the tooltip must make that clear.
    helpText:
      campaignAdType === 'banner_off_site'
        ? t('rm:metrics.clicksOffsite.tooltip')
        : t('rm:metrics.clicks.tooltip'),
    sortKey: 'clicks',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { clicks: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.clicks)
  },
  ctr: {
    title: t('rm:metrics.ctr'),
    helpContent: <MetricTooltip metric="ctr" />,
    optional: true,
    sortKey: 'ctr',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { ctr: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.ctr)
  },
  totalSpent: {
    title:
      accountType === 'publisher'
        ? t('rm:metrics.adsRevenue')
        : t('rm:metrics.investment'),
    helpText:
      accountType === 'publisher'
        ? t('rm:metrics.adsRevenue.tooltip')
        : t('rm:metrics.investment.tooltip'),
    sortKey: 'total_spent',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { totalSpent: any } }) =>
      toggleValueByStatus(rowData.status, rowData?.metrics?.totalSpent)
  },
  conversion: {
    title: t('rm:metrics.conversions'),
    headerSuffix: showClickAlert ? <ConversionsCutoffAlertIcon /> : undefined,
    helpText: t('rm:metrics.conversions.tooltip'),
    sortKey: 'conversions',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { conversions: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.conversions)
  },
  conversion_rate: {
    title: t('rm:metrics.conversion_rate'),
    headerSuffix: showClickAlert ? (
      <ConversionRateCutoffAlertIcon />
    ) : undefined,
    helpContent: (
      <MetricTooltip
        metric="conversion_rate"
        values={{ date: formatRoasCutoffDate(i18n.language) }}
        learnMoreHref={t('rm:alerts.conversionUpdate.link')}
      />
    ),
    sortKey: 'conversion_rate',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { conversionRate: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.conversionRate)
  },
  total_sales: {
    title: t('rm:metrics.sales'),
    helpText: t('rm:metrics.sales.tooltip'),
    sortKey: 'income',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { income: any } }) =>
      toggleValueByStatus(rowData.status, rowData?.metrics?.income)
  },
  total_conversions_items_quantity: {
    title: t('rm:metrics.SoldItems'),
    helpText: t('rm:metrics.SoldItems.tooltip'),
    sortKey: 'total_conversions_items_quantity',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: {
      status: string
      metrics: { totalConversionsItemsQuantity: any }
    }) =>
      toggleValueByStatus(
        rowData.status,
        rowData.metrics.totalConversionsItemsQuantity
      )
  },
  roas: {
    title: t('rm:metrics.roas'),
    helpContent: (
      <MetricTooltip
        metric="roas"
        values={{ date: formatRoasCutoffDate(i18n.language) }}
        learnMoreHref={t('rm:alerts.roasUpdate.link')}
      />
    ),
    sortKey: 'roas',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { roas: any; raw: any } }) =>
      toggleValueByStatus(
        rowData.status,
        <RoasMetricCell
          metric="roas"
          value={rowData.metrics.roas}
          raw={rowData.metrics.raw}
        />
      )
  },
  ...(availableMetrics.impressionShare
    ? {
        impressionShare: {
          title: t('rm:metrics.impressionShare'),
          helpContent: <MetricTooltip metric="impressionShareAd" />,
          helpText: t('rm:metrics.impressionShare.tooltip'),
          sortKey: 'impression_share',
          sortInitialToggleDirection: 'DESC',
          optional: true,
          render: (rowData: {
            status: string
            metrics: { impressionShare: any; raw?: Record<string, unknown> }
          }) => {
            if (['pending_review', 'rejected'].includes(rowData?.status)) {
              return '-'
            }

            return (
              <ImpressionShareMetricCell
                value={rowData?.metrics?.impressionShare}
                raw={rowData?.metrics?.raw}
              />
            )
          }
        }
      }
    : {}),
  ...(availableMetrics.lostIsRank
    ? {
        lostIsRank: {
          title: t('rm:metrics.lostIsRank'),
          helpContent: <MetricTooltip metric="lostIsRank" />,
          helpText: t('rm:metrics.lostIsRank.tooltip'),
          sortKey: 'lost_is_rank',
          sortInitialToggleDirection: 'DESC',
          optional: true,
          render: (rowData: {
            status: string
            metrics: { lostIsRank: any; raw?: Record<string, unknown> }
          }) => {
            if (['pending_review', 'rejected'].includes(rowData?.status)) {
              return '-'
            }

            return (
              <ImpressionShareMetricCell
                value={rowData?.metrics?.lostIsRank}
                raw={rowData?.metrics?.raw}
              />
            )
          }
        }
      }
    : {}),
  ...(availableMetrics.lostIsBudget
    ? {
        lostIsBudget: {
          title: t('rm:metrics.lostIsBudget'),
          helpContent: <MetricTooltip metric="lostIsBudget" />,
          helpText: t('rm:metrics.lostIsBudget.tooltip'),
          sortKey: 'lost_is_budget',
          sortInitialToggleDirection: 'DESC',
          optional: true,
          render: (rowData: {
            status: string
            metrics: { lostIsBudget: any; raw?: Record<string, unknown> }
          }) => {
            if (['pending_review', 'rejected'].includes(rowData?.status)) {
              return '-'
            }

            return (
              <ImpressionShareMetricCell
                value={rowData?.metrics?.lostIsBudget}
                raw={rowData?.metrics?.raw}
              />
            )
          }
        }
      }
    : {}),
  ...ntbAdColumns({
    availableMetrics,
    ntbValues: buildNtbTooltipValues({
      lookbackMonths,
      ordersWindow: ntbOrdersWindow
    })
  }),
  adCost: {
    title: t('rm:metrics.adCost'),
    sortKey: 'adcost',
    sortInitialToggleDirection: 'DESC',
    render: ({
      metrics,
      status
    }: {
      metrics: { adcost: any }
      status: string
    }) => toggleValueByStatus(status, metrics.adcost)
  },
  roas_view: {
    title: t('rm:metrics.roasView'),
    helpText: t('rm:metrics.roasView.tooltip'),
    sortKey: 'roas_view',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { roasView: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.roasView)
  },
  halo_revenue: {
    title: t('rm:metrics.haloRevenue'),
    helpContent: <MetricTooltip metric="haloRevenue" />,
    optional: true,
    sortKey: 'halo_revenue',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { haloRevenue: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.haloRevenue)
  },
  halo_orders: {
    title: t('rm:metrics.haloOrders'),
    helpContent: <MetricTooltip metric="haloOrders" />,
    optional: true,
    sortKey: 'halo_orders',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { haloOrders: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.haloOrders)
  },
  halo_items: {
    title: t('rm:metrics.haloItems'),
    helpContent: <MetricTooltip metric="haloItems" />,
    optional: true,
    sortKey: 'halo_items',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { haloItems: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.haloItems)
  },
  roas_halo: {
    title: t('rm:metrics.roasHalo'),
    helpContent: <MetricTooltip metric="roasHalo" />,
    optional: true,
    sortKey: 'roas_halo',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: { status: string; metrics: { roasHalo: any } }) =>
      toggleValueByStatus(rowData.status, rowData.metrics.roasHalo)
  },
  roas_overall: {
    title: t('rm:metrics.roasOverall'),
    helpContent: <MetricTooltip metric="roasOverall" />,
    optional: true,
    sortKey: 'roas_overall',
    sortInitialToggleDirection: 'DESC',
    render: (rowData: {
      status: string
      metrics: { roasOverall: any; raw: any }
    }) =>
      toggleValueByStatus(
        rowData.status,
        <RoasMetricCell
          metric="overall_roas"
          value={rowData.metrics.roasOverall}
          raw={rowData.metrics.raw}
        />
      )
  }
})
